import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';

// Genera un sonido sutil y nítido de notificación usando Web Audio API sin dependencias externas
const playChimeSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(587.33, now); // Nota D5
    osc.frequency.setValueAtTime(880, now + 0.12); // Nota A5

    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.38);
  } catch (e) {
    // Ignorar si el navegador bloquea audio antes de interacción
  }
};

export const useSupportGlobalHub = ({ token, selectedTicketId, onNavigateToTicket }) => {
  const [incomingToast, setIncomingToast] = useState(null);
  const selectedTicketRef = useRef(selectedTicketId);
  selectedTicketRef.current = selectedTicketId;

  const navigateRef = useRef(onNavigateToTicket);
  navigateRef.current = onNavigateToTicket;

  const handleIncomingActivity = useCallback(async (data) => {
    if (!data) return;

    console.log('🔔 [SupportGlobalHub] Actividad en tiempo real recibida:', data);

    const idTicket = Number(data.idTicket || data.ticketId || data.message?.idTicket);
    if (!idTicket) return;

    const msg = data.message || data;
    const isClient = msg.bEsAgente === 0 || msg.bEsAgente === false || msg.isAgent === false;

    const currentOpenId = Number(selectedTicketRef.current);
    const isCurrentlyOpen = Boolean(currentOpenId && currentOpenId === idTicket);

    // Disparar evento DOM para que las vistas activas (TicketsView, etc.) se sincronicen reactivamente
    window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
      detail: {
        idTicket,
        vTextoMensaje: msg.vTextoMensaje || msg.textoMensaje || msg.messageText || '',
        dUltimaActividad: msg.dCreatedDate || msg.createdDate || new Date().toISOString(),
        dFechaRespuestaInicial: msg.dFechaRespuestaInicial,
        bEsAgente: msg.bEsAgente ?? (msg.isAgent ? 1 : 0),
        iEstadoTicket: data.iEstadoTicket || msg.iEstadoTicket,
        isCurrentlyOpen
      }
    }));

    // Si el mensaje fue enviado por el propio agente o bot, no emitir notificaciones al usuario
    if (!isClient) return;

    // Si el usuario ya tiene abierto este ticket en pantalla, el chat ya se actualiza en vivo: no alertar
    if (isCurrentlyOpen) {
      return;
    }

    // Obtener detalles del mensaje y cliente
    const customerName = msg.vCustomerName || msg.createdUser || `Cliente Ticket #${idTicket}`;
    const messageText = msg.vTextoMensaje || msg.textoMensaje || msg.messageText || 'Nuevo mensaje recibido';

    // 1. Sonido de alerta
    playChimeSound();

    // 2. Si el usuario NO está en la ventana (minimizó o está en otra pestaña/app), disparar Notificación Nativa de Windows
    const isWindowBackground = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());
    if (isWindowBackground && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const notif = new Notification(`Ticket #${idTicket} · ${customerName}`, {
          body: messageText,
          icon: '/favicon.ico',
          tag: `ticket-${idTicket}`,
          renotify: true
        });

        notif.onclick = () => {
          window.focus();
          if (navigateRef.current) {
            navigateRef.current(idTicket);
          }
          notif.close();
        };
      } catch (err) {
        console.warn('[SupportGlobalHub] Error mostrando notificación nativa:', err);
      }
    }

    // 3. Mostrar Toast flotante interactivo en pantalla
    setIncomingToast({
      idTicket,
      customerName,
      messageText,
      timestamp: Date.now()
    });
  }, []);

  // Conexión persistente de SignalR a nivel módulo Support Engine
  useEffect(() => {
    if (!token) return;

    let isCancelled = false;
    let connection = null;

    const startHub = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (!resp.ok || isCancelled) return;
        const { baseUrl } = await resp.json();
        if (isCancelled || !baseUrl) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        connection.on('ReceiveTicketActivity', handleIncomingActivity);

        await connection.start();
      } catch (err) {
        console.warn('[SupportGlobalHub] Conexión SignalR centralizada omitida:', err.message);
      }
    };

    startHub();

    return () => {
      isCancelled = true;
      if (connection) {
        connection.stop().catch(() => {});
      }
    };
  }, [token, handleIncomingActivity]);

  const dismissToast = useCallback(() => {
    setIncomingToast(null);
  }, []);

  return {
    incomingToast,
    dismissToast
  };
};

export default useSupportGlobalHub;
